import type { Therapist } from "api/models/Therapist";
import { UserRole } from "api/models/User";
import type { Market } from "types";
import { isTypeMarket } from "types";

export type UserProfile = {
  roles: UserRole[];
  market?: string;
};

const isUserMarket = (market: Market | Market[], profile: UserProfile | Therapist): boolean => {
  if (!profile.market || !isTypeMarket(profile.market)) {
    return false;
  }
  let checkMarket = market;
  if (!Array.isArray(checkMarket)) {
    checkMarket = [market] as Market[];
  }
  return checkMarket.includes(profile.market);
};

const isUserOfType = (roles: UserRole[], profile?: UserProfile): boolean => {
  if (!profile || !Array.isArray(profile.roles) || !Array.isArray(roles)) {
    return false;
  }

  return roles.some(role => profile.roles.includes(role));
};

const userIsAdmin = (profile?: UserProfile): boolean => isUserOfType([UserRole.HCPAdmin], profile);
const userIsBillingAdmin = (profile?: UserProfile): boolean => isUserOfType([UserRole.BillingAdmin], profile);
const userIsPartner = (profile?: UserProfile): boolean => isUserOfType([UserRole.Partner], profile);
const userIsPatient = (profile?: UserProfile): boolean => isUserOfType([UserRole.Patient], profile);
const userIsPhysician = (profile?: UserProfile): boolean => isUserOfType([UserRole.Physician], profile);
const userIsTherapist = (profile?: UserProfile): boolean => isUserOfType([UserRole.Therapist], profile);
const hasHCO = (profile?: Therapist): boolean => !!profile?.health_care_organization;

const ROUTES = {
  Admin: "/admin",
  AdminBilling: "/admin-billing",
  Calendar: "/calendar",
  Dashboard: "/dashboard",
  Invitations: "/invitations",
  Library: "/library",
  Login: "/login",
  Messages: "/messages",
  Onboarding: "/onboarding",
  Patients: "/patients",
  Settings: "/settings",
  Wiki: "/wiki",
} as const;

const publicPaths = [ROUTES.Login, ROUTES.Onboarding];
const patientFacingPaths = [ROUTES.Dashboard, ROUTES.Patients];
const billingAdminOnlyPaths = [ROUTES.Admin, ROUTES.AdminBilling];
const therapistsOnlyPaths = [
  ...patientFacingPaths,
  ROUTES.Wiki,
  ROUTES.Calendar,
  ROUTES.Invitations,
  ROUTES.Messages,
  ROUTES.Settings,
  ROUTES.Library,
];
const partnerOnlyPaths = [...patientFacingPaths, ROUTES.Invitations, ROUTES.Wiki, ROUTES.Admin];
const adminOnlyPaths = [ROUTES.Admin, ROUTES.AdminBilling, ROUTES.Calendar, ROUTES.Settings, ROUTES.Wiki];
const physicianOnlyPaths = [...patientFacingPaths, ROUTES.Wiki];

const matchesRoute = (path: string, route: string): boolean => path === route || path.startsWith(`${route}/`);

const isRouteAllowed = (profile: UserProfile, path: string): boolean => {
  if (path === "/" || publicPaths.some(p => matchesRoute(path, p))) {
    return true;
  }
  if (profile) {
    if (userIsAdmin(profile) && adminOnlyPaths.some(p => matchesRoute(path, p))) {
      return true;
    }
    if (userIsBillingAdmin(profile) && billingAdminOnlyPaths.some(p => matchesRoute(path, p))) {
      return true;
    }
    if (userIsPartner(profile) && partnerOnlyPaths.some(p => matchesRoute(path, p))) {
      return true;
    }
    if (userIsPhysician(profile) && physicianOnlyPaths.some(p => matchesRoute(path, p))) {
      return true;
    }
    if (userIsTherapist(profile) && therapistsOnlyPaths.some(p => matchesRoute(path, p))) {
      return true;
    }
  }
  return false;
};

export const initialRouteForProfile = (profile: Therapist) => {
  if (profile) {
    if (userIsAdmin(profile)) {
      return profile.therapist_profile?.feature_flags?.includes("BOOKING_V2") ? ROUTES.Calendar : ROUTES.Admin;
    }
    if (userIsBillingAdmin(profile)) {
      return ROUTES.Admin;
    }
  }
  return ROUTES.Dashboard;
};

export {
  isRouteAllowed,
  isUserMarket,
  isUserOfType,
  hasHCO,
  userIsAdmin,
  userIsBillingAdmin,
  userIsPartner,
  userIsPatient,
  userIsPhysician,
  userIsTherapist,
};
